import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { MountSwapAnimation } from '../components/mount/MountSwapAnimation';
import { RotationSystem } from '../components/mount/RotationSystem';
import { PositionComparison } from '../components/mount/PositionComparison';
import { mountApplications } from '../data/mountSystem';
import { useShopNavigation } from '../hooks/useShopNavigation';
import { buttonClasses } from '../utils/buttonStyles';
import { track } from '../utils/analytics';
const generations = [{
  name: 'M1 PRO',
  status: 'Available',
  body: 'Current generation. Ships with the Magnetic Mount System base.',
  current: true
}, {
  name: 'M2 PRO',
  status: 'In development',
  body: 'Future generation designed around the same mounting standard.'
}, {
  name: 'M3 PRO',
  status: 'In development',
  body: 'Future generation designed around the same mounting standard.'
}];
export function MagneticMount() {
  const shop = useShopNavigation();
  return <>
      {/* Hero */}
      <header className="mf-tech-grid border-b mf-hairline bg-deep" data-id="element-1233" data-mp="2xy6dv2de68f5">
        <div className="mx-auto grid max-w-site items-center gap-10 px-5 pb-14 pt-14 md:px-8 md:pb-20 md:pt-20 lg:grid-cols-[1fr_1.1fr] lg:gap-16" data-id="element-1234" data-mp="2xvafxe1jnjqp">
          <div data-id="element-1235" data-mp="2ovx6umhc0x6i">
            <p className="font-display text-base uppercase tracking-[0.16em] text-titanium md:text-lg" data-id="element-1236" data-mp="2ovf0uy4vb4le">Magnetic Mount System™</p>
            <h1 className="mt-3 font-display text-5xl font-semibold uppercase leading-[0.92] md:text-7xl" data-id="element-1237" data-mp="2k07zvy2zge06">
              One Mount. One Perfect Position. <span className="text-ice" data-id="element-1238" data-mp="2a7tv0wglyr9s">Every Time.</span>
            </h1>
            <p className="mt-6 font-display text-xl font-semibold uppercase tracking-[0.06em] text-white md:text-2xl" data-id="element-1239" data-mp="258ppcfsw7bo7">
              Your lights come off. Your mounts stay put.
            </p>
            <p className="mt-5 max-w-xl text-lg text-titanium" data-id="element-1240" data-mp="1w8azr48wbaew">
              The base stays attached exactly where you installed it. When the M1 PRO needs charging, lift it off. When
              it’s ready, a charged light snaps magnetically onto that same position.
            </p>
            <p className="mt-5 text-sm font-semibold uppercase tracking-[0.12em] text-ice" data-id="element-1241" data-mp="1hnyilgobd9m3">
              No guessing · No re-measuring · No remounting
            </p>
            <button type="button" onClick={() => {
            track('mount_cta', {
              location: 'hero'
            });
            shop();
          }} className={`${buttonClasses('primary', 'lg')} mt-8 w-full sm:w-auto`} data-id="element-1242" data-mp="17oz8dtnwh5wz">
              Shop the M1 PRO Systems <ArrowRightIcon className="h-4 w-4" aria-hidden data-id="element-1243" data-mp="25an74l7tw0bo" />
            </button>
          </div>
          <MountSwapAnimation data-id="element-1244" data-mp="1qlqzgp9oopbl" />
        </div>
      </header>

      {/* 8-Light rotation */}
      <section aria-labelledby="rotation-title-h" className="border-b mf-hairline bg-navy" data-id="element-1245" data-mp="1vk28ixs5mvsr">
        <div className="mx-auto max-w-site px-5 py-14 md:px-8 md:py-20" data-id="element-1246" data-mp="2ja60u8zw794g">
          <h2 id="rotation-title-h" className="max-w-4xl font-display text-4xl font-semibold uppercase leading-[0.95] md:text-6xl" data-id="element-1247" data-mp="2o27o0pylmk99">
            Four permanent positions. <span className="text-ice" data-id="element-1248" data-mp="ckm7cs5o2bjd">Eight ready-to-use lights.</span>
          </h2>
          <div className="mt-8" data-id="element-1249" data-mp="7ncairfy6xli">
            <RotationSystem data-id="element-1250" data-mp="mcigcy9ajm2d" />
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section aria-labelledby="compare-title" className="bg-deep" data-id="element-1251" data-mp="hdffzr49a40q">
        <div className="mx-auto max-w-site px-5 py-14 md:px-8 md:py-20" data-id="element-1252" data-mp="he76inizxbk5">
          <h2 id="compare-title" className="max-w-3xl font-display text-4xl font-semibold uppercase leading-[0.95] md:text-6xl" data-id="element-1253" data-mp="3pu0dobrftaoj">
            The position stays. <span className="text-ice" data-id="element-1254" data-mp="2oj93xdxqx1p">The light changes.</span>
          </h2>
          <div className="mt-8" data-id="element-1255" data-mp="2mesceuxu3k0">
            <PositionComparison data-id="element-1256" data-mp="3usk4oqkw1cnr" />
          </div>
        </div>
      </section>

      {/* Applications */}
      <section aria-labelledby="mount-apps-title" className="border-t mf-hairline bg-deep" data-id="element-1257" data-mp="1e353mce7xlkh">
        <div className="mx-auto max-w-site px-5 py-14 md:px-8 md:py-20" data-id="element-1258" data-mp="1a2cs1chvj4l1">
          <h2 id="mount-apps-title" className="max-w-3xl font-display text-4xl font-semibold uppercase leading-[0.95] md:text-6xl" data-id="element-1259" data-mp="1a4fhnvf46vp8">
            Every space. <span className="text-ice" data-id="element-1260" data-mp="29ew8hkw0fwa7">A layout that stays organized.</span>
          </h2>
          <ul className="mt-8 grid gap-4 sm:grid-cols-2" data-id="element-1261" data-mp="2ecobb9y712t6">
            {mountApplications.map(app => <li key={app.title} className="relative min-h-[300px] overflow-hidden md:min-h-[380px]" data-id="element-1262" data-mp="2jcam7cqju6yt">
                <img src={app.image} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" data-id="element-1263" data-mp="2oa2h8fmhq978" />
                <div className="absolute inset-x-0 bottom-0 bg-[#030A14]/80 p-5 backdrop-blur-sm" data-id="element-1264" data-mp="2sb5ucud5lr4r">
                  <h3 className="font-display text-2xl font-semibold uppercase tracking-[0.04em]" data-id="element-1265" data-mp="2x3z11ns6n9jh">{app.title}</h3>
                  <p className="mt-1 text-sm text-titanium" data-id="element-1266" data-mp="321qtqk0nizoq">{app.body}</p>
                </div>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Long-term vision */}
      <section aria-labelledby="future-title" className="border-t mf-hairline bg-navy" data-id="element-1267" data-mp="3bylnyqpw6dx7">
        <div className="mx-auto max-w-site px-5 py-14 md:px-8 md:py-20" data-id="element-1268" data-mp="10env6wdvup26">
          <h2 id="future-title" className="font-display text-3xl font-semibold uppercase md:text-4xl" data-id="element-1269" data-mp="vd886ys5a8h5">
            One Mount. <span className="text-ice" data-id="element-1270" data-mp="qfovw4f0c5ru">Every Generation.</span>
          </h2>
          <ol className="mt-10 grid gap-px overflow-hidden border mf-hairline bg-white/10 md:grid-cols-3" data-id="element-1271" data-mp="lgvdpyorzqlc">
            {generations.map(g => <li key={g.name} className={`p-7 ${g.current ? 'bg-[#0D2440]' : 'bg-navy'}`} data-id="element-1272" data-mp="1j5x8lgqrxxz1">
                <span className={`text-xs font-semibold uppercase tracking-[0.14em] ${g.current ? 'text-marine' : 'text-titanium/60'}`} data-id="element-1273" data-mp="1e6uakg97os0k">
                  {g.status}
                </span>
                <h3 className={`mt-2 font-display text-4xl font-semibold uppercase ${g.current ? 'text-white' : 'text-titanium'}`} data-id="element-1274" data-mp="14eo98fx2ky8p">
                  {g.name}
                </h3>
                <p className="mt-2 text-sm text-titanium" data-id="element-1275" data-mp="1x2tud3s1cp92">{g.body}</p>
              </li>)}
          </ol>
        </div>
      </section>

      {/* Final CTA */}
      <section aria-labelledby="mount-cta-title" className="mf-tech-grid border-t mf-hairline bg-deep" data-id="element-1276" data-mp="5o5cng9vcdu">
        <div className="mx-auto flex max-w-site flex-col items-center px-5 py-16 text-center md:px-8 md:py-20" data-id="element-1277" data-mp="3hm5xxrgfse55">
          <h2 id="mount-cta-title" className="max-w-4xl font-display text-4xl font-semibold uppercase leading-[0.95] md:text-6xl" data-id="element-1278" data-mp="3mjpe4wwp30zq">
            Install once. Swap in seconds. <span className="text-ice" data-id="element-1279" data-mp="3cml1posnijw4">Enjoy again and again.</span>
          </h2>
          <button type="button" onClick={() => {
          track('mount_cta', {
            location: 'final'
          });
          shop();
        }} className={`${buttonClasses('primary', 'lg')} mt-10 w-full sm:w-auto`} data-id="element-1280" data-mp="3ck89mit6it5c">
            Shop the M1 PRO Systems <ArrowRightIcon className="h-4 w-4" aria-hidden data-id="element-1281" data-mp="ww8cg4a7ej9p" />
          </button>
        </div>
      </section>
    </>;
}